import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Building2, Target, Clock, TrendingUp, Link2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

interface DealContext {
  asset_type?: string;
  unit_count?: string;
  exit_strategy?: string;
  hold_period?: string;
  target_irr_band?: string;
  occupancy_current?: string;
}

const dealPages = ["/context", "/quick-screen", "/results", "/assumptions", "/sensitivity", "/export"];

export function DealContextBar() {
  const location = useLocation();
  const [ctx, setCtx] = useState<DealContext | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("brickshare_context");
    if (raw) {
      try { setCtx(JSON.parse(raw)); } catch { setCtx(null); }
    } else {
      setCtx(null);
    }
  }, [location.pathname]);

  if (!ctx || !dealPages.includes(location.pathname)) return null;

  const dealId = sessionStorage.getItem("brickshare_deal_id");

  const handleCopyLink = async () => {
    if (!dealId) return;
    const url = `${window.location.origin}${location.pathname}?deal=${dealId}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast({ title: "Link copied", description: "Share this URL with colleagues to view this deal." });
    setTimeout(() => setCopied(false), 2000);
  };

  const pills = [
    { icon: Building2, label: ctx.asset_type || "Multifamily", show: true },
    { icon: Target, label: `${ctx.unit_count || "—"} units`, show: !!ctx.unit_count },
    { icon: TrendingUp, label: ctx.exit_strategy || "Value-Add", show: true },
    { icon: Clock, label: `${ctx.hold_period || "—"} yr hold`, show: !!ctx.hold_period },
  ].filter(p => p.show);

  return (
    <div className="h-10 flex items-center gap-3 px-4 border-b border-[hsl(var(--stone-200))]/70 bg-[hsl(var(--bone))]/70 backdrop-blur-sm">
      <span className="eyebrow text-[hsl(var(--stone-600))]">Active Deal</span>
      <span className="w-6 h-px bg-[hsl(var(--brand-ochre))]" />
      {pills.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <p.icon className="w-3 h-3 text-[hsl(var(--brand-forest))]" />
          <span className="font-mono text-[11px] text-[hsl(var(--stone-600))]">{p.label}</span>
        </div>
      ))}
      {dealId && (
        <>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2.5 text-2xs font-mono uppercase tracking-[0.1em] gap-1.5 text-[hsl(var(--stone-600))] hover:text-[hsl(var(--ink))] hover:bg-[hsl(var(--muted))]"
            onClick={handleCopyLink}
          >
            {copied ? <Check className="w-3 h-3 text-[hsl(var(--brand-forest))]" /> : <Link2 className="w-3 h-3" />}
            {copied ? "Copied" : "Share Link"}
          </Button>
        </>
      )}
    </div>
  );
}
